import React, { useEffect, useRef, useState } from "react";
import { Message } from "../../interfaces/chat.interface";
import { ChatMessage } from "../chat/chat-message";
import { Skeleton } from "../loader/skeleton";
import aiIcon from "@/assets/ai-logo.svg";
import { Citation } from "../../constants/citation.constant";
import { CircleArrowDownIcon } from "lucide-react";

/**
 * Props for the ChatMessages component
 */
interface ChatMessagesProps {
  /** Array of messages to display in the conversation */
  displayConversation: Message[] | null;
  /** Whether a response is currently being generated */
  isResponseGenerating: boolean;
  /** Whether messages are being loaded */
  isLoading: boolean;
  /** ID of the conversation currently streaming */
  streamingConversationId: string | null;
  /** ID of the selected conversation */
  selectedConversationId?: string;
  /** Whether the sources modal is open */
  sourcesOpen: boolean;
  /** Index of the active message showing sources */
  activeMessageIndex: number | null;
  /** Callback fired when sources are opened for a message */
  onOpenSources: (sources: Citation[], messageIndex: number) => void;
  /** Callback fired when sources are closed */
  onCloseSources: () => void;
}

/**
 * ChatMessages component displays a scrollable list of chat messages with auto-scroll
 * functionality. Handles streaming messages, loading states, and source citations.
 * Shows a scroll-to-bottom button when user scrolls up during streaming.
 * 
 * @component
 * @param {ChatMessagesProps} props - Component props
 * @param {Message[] | null} props.displayConversation - Messages to display
 * @param {boolean} props.isResponseGenerating - Whether response is generating
 * @param {boolean} props.isLoading - Whether messages are loading
 * @param {string | null} props.streamingConversationId - ID of streaming conversation
 * @param {string} [props.selectedConversationId] - Selected conversation ID
 * @param {boolean} props.sourcesOpen - Whether sources modal is open
 * @param {number | null} props.activeMessageIndex - Active message index
 * @param {(sources: Citation[], messageIndex: number) => void} props.onOpenSources - Open sources callback
 * @returns {JSX.Element | null} Scrollable message container or null if no messages
 * 
 * @example
 * ```tsx
 * <ChatMessages
 *   displayConversation={messages}
 *   isResponseGenerating={isGenerating}
 *   onOpenSources={handleOpenSources}
 * />
 * ```
 */
export const ChatMessages: React.FC<ChatMessagesProps> = ({
  displayConversation,
  isResponseGenerating,
  isLoading,
  streamingConversationId,
  selectedConversationId,
  sourcesOpen,
  activeMessageIndex,
  onOpenSources,
  onCloseSources,
}) => {
  const [autoScroll, setAutoScroll] = useState<boolean>(true);
  const containerRef = useRef<null | HTMLDivElement>(null);
  const bottomRef = useRef<null | HTMLDivElement>(null);
  const lastScrollTop = useRef(0);

  useEffect(() => {
    if (!autoScroll && streamingConversationId != selectedConversationId) {
      setAutoScroll(true);
    }
    if (autoScroll && bottomRef.current) {
      setTimeout(() => {
        bottomRef.current &&
          bottomRef.current.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [displayConversation, selectedConversationId]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const { scrollTop } = el;

    // Direction detection
    const goingUp = scrollTop < lastScrollTop.current;
    lastScrollTop.current = scrollTop;

    if (goingUp && streamingConversationId == selectedConversationId) {
      // user is scrolling up -> stop auto scroll *right away*
      if (autoScroll) setAutoScroll(false);
      return;
    }
  };

  if (!displayConversation || displayConversation.length === 0) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className="flex-1 relative overflow-y-auto overflow-x-hidden"
    >
      {displayConversation.map((message, index) => {
        const isLast = index === displayConversation.length - 1;
        const hideEmptyAssistant =
          isResponseGenerating &&
          isLast &&
          message.role === "assistant" &&
          (!message.content || message.content.trim() === "") &&
          (!message.reasoning || message.reasoning.trim() === "");

        if (hideEmptyAssistant) return null;

        return (
          <ChatMessage
            key={index}
            message={message}
            messageIndex={index}
            onOpenSources={(sources) => onOpenSources(sources, index)}
            onCloseSources={onCloseSources}
            sourcesOpen={sourcesOpen && activeMessageIndex === index}
            isStreaming={
              isResponseGenerating &&
              isLast &&
              streamingConversationId == selectedConversationId
            }
          />
        );
      })}
      {isResponseGenerating &&
        streamingConversationId == selectedConversationId &&
        !autoScroll && (
          <div
            className="flex fixed w-full justify-center z-index-[10] md:left-[140px] bottom-[calc(0svh+175px)] cursor-pointer"
            onClick={() => setAutoScroll(true)}
          >
            <CircleArrowDownIcon className="text-primary-500 bg-primary-50 hover:bg-[#e8effa] rounded-full h-10 w-10 p-1"></CircleArrowDownIcon>
          </div>
        )}
      {isLoading && selectedConversationId === streamingConversationId && (
        <div
          className="group px-4 text-gray-800 dark:text-gray-100"
          style={{ overflowWrap: "anywhere" }}
        >
          <div className="m-auto flex items-start gap-4 md:p-4 p-3 text-base md:max-w-2xl md:gap-6 md:py-4 lg:max-w-2xl lg:px-0 lg:max-w-[56rem]">
            {/* Logo - hidden on mobile, shown on desktop */}
            <div className="hidden md:flex flex-shrink-0 justify-start items-start pt-2">
              <div className="w-8 h-8 flex items-center justify-center">
                <img
                  src={aiIcon}
                  alt="CHOP AI"
                  className="w-8 h-8 object-contain"
                />
              </div>
            </div>
            <div className="flex flex-shrink-0 justify-start items-start pt-2 md:ml-0">
              <Skeleton />
            </div>
          </div>
        </div>
      )}
      <div ref={bottomRef} className={`${isResponseGenerating ? 'mb-10': ''}`}/>
    </div>
  );
};
